import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, UserCog } from "lucide-react";

const ROLE_LABELS = {
  dueno: "Dueño", administracion: "Administración", operacion: "Operación", consulta: "Consulta",
};
const EMPTY = { nombre_completo: "", email: "", password: "", rol: "consulta" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ nombre_completo: u.nombre_completo, email: u.email, rol: u.rol, password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { nombre_completo: form.nombre_completo, rol: form.rol });
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", form);
        toast.success("Usuario creado");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleActivo = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { activo: !u.activo });
      toast.success(u.activo ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Usuarios"
        subtitle="Gestión de usuarios de la plataforma (solo Dueño)"
        actions={<Button onClick={openCreate} data-testid="new-user-btn" className="gap-2"><Plus className="h-4 w-4" /> Nuevo usuario</Button>}
      />
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Último acceso</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.nombre_completo}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell><Badge variant="outline">{ROLE_LABELS[u.rol]}</Badge></TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString("es-MX") : "—"}
                </TableCell>
                <TableCell>
                  {u.activo
                    ? <Badge className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-50">Activo</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactivo</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>Editar</Button>
                    <div className="flex items-center gap-2">
                      <Switch checked={u.activo} onCheckedChange={() => toggleActivo(u)} data-testid={`toggle-user-${u.email}`} />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 font-heading">
              <UserCog className="h-5 w-5" /> {editing ? "Editar usuario" : "Nuevo usuario"}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Nombre completo</Label>
              <Input className="mt-1" value={form.nombre_completo} onChange={(e) => setForm({ ...form, nombre_completo: e.target.value })} data-testid="user-nombre" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input className="mt-1" type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
            </div>
            {!editing && (
              <div>
                <Label className="text-xs text-muted-foreground">Contraseña inicial</Label>
                <Input className="mt-1" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">Rol</Label>
              <Select value={form.rol} onValueChange={(v) => setForm({ ...form, rol: v })}>
                <SelectTrigger className="mt-1" data-testid="user-rol"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dueno">Dueño</SelectItem>
                  <SelectItem value="administracion">Administración</SelectItem>
                  <SelectItem value="operacion">Operación</SelectItem>
                  <SelectItem value="consulta">Consulta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.nombre_completo || (!editing && (!form.email || !form.password))} data-testid="user-save-btn">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
